import { useState, useEffect, useCallback, useMemo } from 'react'
import { MealsApi } from '@/shared/api/meals'
import { TripMemberDietaryProfile } from '@/shared/types'
import { useAuth } from '@/modules/auth'

export type MemberDietaryProfile = TripMemberDietaryProfile

export interface UseTripMembersDietaryRestrictionsReturn {
  membersProfiles: Record<string, MemberDietaryProfile>
  allProfiles: MemberDietaryProfile[]
  isLoading: boolean
  error: string | null
  refreshRestrictions: () => Promise<void>
}

export function useTripMembersDietaryRestrictions(
  tripId: string | null | undefined,
  options?: { skip?: boolean }
): UseTripMembersDietaryRestrictionsReturn {
  const { getAccessToken, isAuthenticated } = useAuth()
  const [profiles, setProfiles] = useState<TripMemberDietaryProfile[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    return getAccessToken()
  }, [getAccessToken])

  const mealsApi = useMemo(() => new MealsApi(getAuthToken), [getAuthToken])

  // Index profiles by user id for quick lookup by components
  const membersProfiles = useMemo(() => {
    const grouped: Record<string, MemberDietaryProfile> = {}
    if (Array.isArray(profiles)) {
      profiles.forEach((profile) => {
        if (profile?.user?.id) {
          grouped[profile.user.id] = profile
        }
      })
    }
    return grouped
  }, [profiles])

  const loadRestrictions = useCallback(async () => {
    if (!tripId || !isAuthenticated || options?.skip) {
      setProfiles([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await mealsApi.getTripDietaryRestrictions(tripId)
      setProfiles(Array.isArray(data) ? data : [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dietary profiles'
      setError(errorMessage)
      setProfiles([])
    } finally {
      setIsLoading(false)
    }
  }, [tripId, mealsApi, isAuthenticated, options?.skip])

  const refreshRestrictions = useCallback(async () => {
    await loadRestrictions()
  }, [loadRestrictions])

  useEffect(() => {
    loadRestrictions()
  }, [loadRestrictions])

  return {
    membersProfiles,
    allProfiles: profiles,
    isLoading,
    error,
    refreshRestrictions
  }
}
